import React, {useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import {useAppSelector, useAppDispatch} from '../hooks/redux';
import {selectCurrentPatient, selectSubPatients, setCurrentPatient} from '../slices/patientSlice';
import {selectTests, fetchTests} from '../slices/testSlice';
import {useAuth} from '../hooks/useAuth';
import {User} from 'lucide-react';

const PatientSwitcher: React.FC = () => {
    const {user, setUser} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Redux state
    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);
    const testsState = useAppSelector(selectTests);

    // Query params
    const queryParams = new URLSearchParams(location.search);
    const ref = queryParams.get('ref');

    // Fetch patient data if not already loaded
    useEffect(() => {
        if (user?.isLoggedIn && subPatients.length === 0 && !testsState.loading) {
            dispatch(fetchTests());
        }
    }, [dispatch, user?.isLoggedIn, subPatients.length, testsState.loading]);

    // Helper function to handle array or string values (from old implementation)
    const getPatientName = (patient: any) => {
        const firstName = Array.isArray(patient.firstName) ? patient.firstName[0] : patient.firstName;
        const lastName = Array.isArray(patient.lastName) ? patient.lastName[0] : patient.lastName;
        return `${firstName} ${lastName}`;
    };

    // Helper function to format birthdate (from old implementation)
    const formatBirthdate = (birthdate: any) => {
        if (typeof birthdate !== 'object') {
            return new Date(birthdate).toLocaleDateString('en-US', {timeZone: 'UTC'});
        }
        return birthdate.toLocaleDateString('en-US', {timeZone: 'UTC'});
    };

    // Handle patient selection (full user impersonation)
    const handlePatientSelect = (patient: any) => {
        // Only store admin data if we're not already impersonating
        if (!user?.userInfo?.impersonate_id) {
            const originalAdminData = {
                token: user?.token,
                userInfo: user?.userInfo,
                username: user?.username,
                switchUser: true, // Flag to distinguish from admin impersonation
            };

            localStorage.setItem('originalAdminData', JSON.stringify(originalAdminData));
        }

        // Full impersonation - update auth context with patient's data
        const newUserData = {
            ...user,
            userInfo: {
                ...user?.userInfo,
                // Override with patient's data for full impersonation
                id: patient.id,
                firstName: patient.firstName,
                lastName: patient.lastName,
                fullName: patient.fullName,
                // We don't have the patient's email/phone/address from test data
                // In a real implementation, we'd fetch this from the API
                // For now, we'll simulate it
                email: `${patient.firstName.toLowerCase()}.${patient.lastName.toLowerCase()}@example.com`,
                // Add impersonation flag so banner shows
                impersonate_id: patient.id,
                isImpersonating: true,
            },
            isLoggedIn: true,
        };

        setUser(newUserData);

        // Also update Redux current patient
        dispatch(setCurrentPatient(patient));

        // Navigate to dashboard
        navigate('/');
    };

    // Check if a patient is currently active
    const isCurrentPatient = (patient: any) => {
        return currentPatient.fullName === patient.fullName && ref !== 'initial';
    };

    return (
        <DashboardLayout>
            <PageHeader
                title="Select A Patient To View"
                subtitle={
                    subPatients.length > 0
                        ? "Choose which patient's data to view and manage"
                        : 'Loading patient data...'
                }
            />

            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="grid gap-4">
                        {subPatients.length > 0 ? (
                            subPatients.map((patient: any) => (
                                <div
                                    key={patient.id}
                                    onClick={() => handlePatientSelect(patient)}
                                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                        isCurrentPatient(patient)
                                            ? 'border-mld-primary bg-mld-primary/5'
                                            : 'border-gray-200 hover:border-mld-primary/50'
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <User className="h-8 w-8 text-mld-primary" />
                                            <div>
                                                <h4 className="font-semibold text-mld-text-primary">
                                                    {getPatientName(patient)}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    DOB: {formatBirthdate(patient.birthdate)}
                                                </p>
                                            </div>
                                        </div>
                                        {isCurrentPatient(patient) && (
                                            <div className="text-xs font-medium text-mld-primary uppercase tracking-wide">
                                                Currently Viewing
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : testsState.loading ? (
                            <div className="text-center py-8 text-gray-500">Loading patients...</div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">No patients found</div>
                        )}
                    </div>
                </div>

                {/* Close link when coming from dashboard (matches old implementation) */}
                {currentPatient.fullName && ref !== 'initial' && (
                    <div className="text-center">
                        <button
                            onClick={() => navigate('/')}
                            className="text-mld-primary hover:text-mld-primary/80 transition-colors font-medium">
                            Close
                        </button>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default PatientSwitcher;
